import { motion } from "motion/react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Download, Sparkles } from "lucide-react";
import { ImageWithFallback } from "../components/figma/ImageWithFallback";
import { caseStudies } from "../data/portfolio";

export default function Home() {
  const featuredCaseStudies = caseStudies.slice(0, 2);
  

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen"
    >
      {/* Hero Section */}
      <section className="pt-32 md:pt-40 pb-20 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="text-center max-w-4xl mx-auto"
          >
            {/* Designer Photo */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="mb-8"
            >
              <div className="relative inline-block">
                <ImageWithFallback
                  src="https://scontent.fdac134-1.fna.fbcdn.net/v/t39.30808-6/469914151_122212848470019737_6234624026396882587_n.jpg?_nc_cat=109&ccb=1-7&_nc_sid=6ee11a&_nc_ohc=zmxHtMhGZpIQ7kNvwHWKGtI&_nc_oc=Adl74F9C1ej4F2f40FmMo8KwmwqjKmf1-pD7sW_AhD4_vX3-T2kL2zOMhUqHei4MY0c&_nc_zt=23&_nc_ht=scontent.fdac134-1.fna&_nc_gid=94Zu_-iKJy995yIbLOwIlA&oh=00_AfZKmpM3urrxYnE9MXVJCfffZLkdJZwBc1sNkxQvVRbaiA&oe=68C904EE"
                  alt="Designer Portrait"
                  className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-2xl"
                />
                <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-purple-600/20 to-blue-600/20" />
              </div>
            </motion.div>

            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <div className="inline-flex items-center space-x-2 bg-purple-50 text-purple-700 px-4 py-2 rounded-full text-sm mb-8">
                <Sparkles className="w-4 h-4" />
                <span>Available for new opportunities</span>
              </div>
            </motion.div>

            <motion.h1
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="text-4xl md:text-6xl lg:text-7xl text-foreground mb-6 leading-tight"
            >
              Designing digital experiences that{" "}
              <span className="bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                delight users
              </span>
            </motion.h1>

            <motion.p
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="text-xl text-muted-foreground mb-12 leading-relaxed max-w-2xl mx-auto"
            >
              I help companies design intuitive products that
              users love. With 5+ years of experience, I
              specialize in UX research, interface design, and
              design systems.
            </motion.p>

            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 1.0 }}
              className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6 mb-16"
            >
              <Link
                to="/case-studies"
                className="inline-flex items-center space-x-2 bg-foreground text-background px-8 py-4 rounded-xl hover:bg-foreground/90 transition-colors group"
              >
                <span>View Case Studies</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>

              <button className="inline-flex items-center space-x-2 border border-border px-8 py-4 rounded-xl hover:bg-accent transition-colors">
                <Download className="w-4 h-4" />
                <span>Download Resume</span>
              </button>
            </motion.div>

            {/* Social Icons */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 1.2 }}
              className="flex items-center justify-center space-x-6"
            >
              <a
                href="https://linkedin.com/in/yourprofile"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-muted/50 hover:bg-foreground hover:text-background rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 group"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>

              <a
                href="https://github.com/yourusername"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-muted/50 hover:bg-foreground hover:text-background rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 group"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                </svg>
              </a>

              <a
                href="mailto:your.email@example.com"
                className="w-12 h-12 bg-muted/50 hover:bg-foreground hover:text-background rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 group"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Featured Work */}
      <section className="py-20 px-4 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl text-foreground mb-4">
              Featured Work
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              A selection of recent projects showcasing my
              approach to solving complex design challenges
              through user research and iterative design.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {featuredCaseStudies.map((study, index) => (
              <motion.div
                key={study.slug}
                initial={{ y: 40, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.6,
                  delay: index * 0.2,
                }}
                className="group"
              >
                <Link
                  to={`/case-studies/${study.slug}`}
                  className="block"
                >
                  <div className="bg-card backdrop-blur-xl border border-border rounded-2xl overflow-hidden hover:shadow-2xl hover:shadow-purple-500/10 transition-all duration-500 group-hover:-translate-y-2">
                    <div className="aspect-[4/3] overflow-hidden">
                      <ImageWithFallback
                        src={study.image}
                        alt={study.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>

                    <div className="p-8">
                      <div className="flex items-center space-x-2 mb-4">
                        <span
                          className={`px-3 py-1 rounded-full text-xs ${
                            study.category === "work"
                              ? "bg-green-100 text-green-700"
                              : "bg-blue-100 text-blue-700"
                          }`}
                        >
                          {study.category === "work"
                            ? "Client Work"
                            : "Research"}
                        </span>
                        <span className="text-muted-foreground text-sm">
                          {study.year}
                        </span>
                      </div>

                      <h3 className="text-xl mb-2 group-hover:text-purple-600 transition-colors">
                        {study.title}
                      </h3>

                      <p className="text-muted-foreground mb-4 line-clamp-2">
                        {study.subtitle}
                      </p>

                      <div className="flex flex-wrap gap-2 mb-6">
                        {study.tags.slice(0, 3).map((tag) => (
                          <span
                            key={tag}
                            className="text-xs bg-muted px-2 py-1 rounded-md text-muted-foreground"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>

                      <div className="flex items-center space-x-2 text-purple-600">
                        <span className="text-sm">
                          Read case study
                        </span>
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-center mt-12"
          >
            <Link
              to="/case-studies"
              className="inline-flex items-center space-x-2 text-purple-600 hover:text-purple-700 transition-colors"
            >
              <span>View all case studies</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="grid lg:grid-cols-2 gap-16 items-center"
          >
            <div>
              <h2 className="text-3xl md:text-4xl text-foreground mb-6">
                Human-centered design philosophy
              </h2>

              <p className="text-muted-foreground mb-6 text-lg leading-relaxed">
                I believe great design starts with deep empathy
                for users and ends with measurable business
                impact. My process combines rigorous research
                with creative problem-solving to create
                experiences that are both delightful and
                functional.
              </p>

              <div className="space-y-4">
                {[
                  "Research-driven decisions backed by user data",
                  "Inclusive design that works for everyone",
                  "Collaboration across disciplines and stakeholders",
                  "Iterative design with continuous improvement",
                ].map((principle, index) => (
                  <motion.div
                    key={index}
                    initial={{ x: -20, opacity: 0 }}
                    whileInView={{ x: 0, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{
                      duration: 0.4,
                      delay: index * 0.1,
                    }}
                    className="flex items-center space-x-3"
                  >
                    <div className="w-2 h-2 bg-purple-600 rounded-full" />
                    <span className="text-muted-foreground">
                      {principle}
                    </span>
                  </motion.div>
                ))}
              </div>

              <Link
                to="/about"
                className="inline-flex items-center space-x-2 mt-8 text-purple-600 hover:text-purple-700 transition-colors"
              >
                <span>Learn more about me</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="relative">
              <div className="aspect-square bg-gradient-to-br from-purple-100 to-blue-100 rounded-3xl overflow-hidden">
                <ImageWithFallback
                  src="https://images.unsplash.com/photo-1671726805768-575f88de945a?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwcm9kdWN0JTIwZGVzaWduJTIwcG9ydGZvbGlvJTIwd29ya3NwYWNlfGVufDF8fHx8MTc1NzYwNTQxN3ww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
                  alt="Design workspace"
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Floating elements */}
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="absolute -top-4 -right-4 w-16 h-16 bg-purple-500 rounded-2xl backdrop-blur-xl border border-white/20 flex items-center justify-center"
              >
                <Sparkles className="w-8 h-8 text-white" />
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-purple-50 to-blue-50">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl text-foreground mb-6">
              Let's create something amazing together
            </h2>

            <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">
              I'm currently available for new opportunities.
              Whether you're looking for a full-time role or
              consulting work, I'd love to hear about your
              project.
            </p>

            <Link
              to="/contact"
              className="inline-flex items-center space-x-2 bg-foreground text-background px-8 py-4 rounded-xl hover:bg-foreground/90 transition-colors group"
            >
              <span>Get in touch</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
}
